import { Box, Link, StatusLabel } from '@factorialco/deprecated-design-system'
import {
  BankingValueObjectsPaymentRequestItemStatusEnum,
  BankingValueObjectsPaymentRequestSourceTypeEnum,
} from 'generated/resources/graphql'

import Sidepanel from 'modules/core/components/Sidepanel'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import CustomTwoColumnsList from 'modules/attendance/components/CustomTwoColumnsList'
import AmountWithCurrency from 'modules/expenses/lib/transactions/AmountWithCurrency'

type StatusColor = 'neutral' | 'info' | 'positive' | 'warning' | 'critical'

const getStatusColor = (
  status: BankingValueObjectsPaymentRequestItemStatusEnum
): StatusColor => {
  switch (status) {
    case 'init':
      return 'warning'
    case 'in_payment':
    case 'in_progress':
      return 'info'
    case 'paid':
      return 'positive'
    case 'rejected':
      return 'critical'
    default:
      return status satisfies never
  }
}

const getStatusLabel = (
  status: BankingValueObjectsPaymentRequestItemStatusEnum
): string => {
  switch (status) {
    case 'init':
      return i18n.t('banking.paymentRequests.itemStatus.init')
    case 'in_payment':
      return i18n.t('banking.paymentRequests.itemStatus.inPayment')
    case 'in_progress':
      return i18n.t('banking.paymentRequests.itemStatus.inProgress')
    case 'paid':
      return i18n.t('banking.paymentRequests.itemStatus.paid')
    case 'rejected':
      return i18n.t('banking.paymentRequests.itemStatus.rejected')
    default:
      return status satisfies never
  }
}

const getSourceTypeLabel = (
  sourceType: BankingValueObjectsPaymentRequestSourceTypeEnum
): string => {
  switch (sourceType) {
    case 'invoice':
      return i18n.t('banking.paymentRequests.itemDetailSidePanel.sourceTypes.invoice')
    case 'payroll':
      return i18n.t('banking.paymentRequests.itemDetailSidePanel.sourceTypes.payroll')
    case 'expensable':
      return i18n.t('banking.paymentRequests.itemDetailSidePanel.sourceTypes.expensable')
    case 'salary_advance_request':
      return i18n.t(
        'banking.paymentRequests.itemDetailSidePanel.sourceTypes.salary_advance_request'
      )
    case 'compensation':
      return i18n.t('banking.paymentRequests.itemDetailSidePanel.sourceTypes.compensation')
    default:
      return sourceType satisfies never
  }
}

const getSourceNavigationPath = (
  sourceType: BankingValueObjectsPaymentRequestSourceTypeEnum,
  sourceId: number | null | undefined,
  navigation: ReturnType<typeof useNavigation>['navigation']
): string | null => {
  if (!sourceId) {
    return null
  }

  switch (sourceType) {
    case 'expensable':
      return navigation.expenses.list.expenses.expenseId.fullPath({
        expense_id: sourceId,
      })
    case 'invoice':
      return navigation.finance.invoices.list.invoiceId.fullPath({
        invoiceId: sourceId,
      })
    case 'salary_advance_request':
      return null
    case 'payroll':
      return null
    case 'compensation':
      return null
    default:
      return sourceType satisfies never
  }
}

export interface PaymentRequestItemData {
  beneficiaryName: string
  beneficiaryAccountNumber: string
  amountCents: number
  currency: string
  status: BankingValueObjectsPaymentRequestItemStatusEnum
  createdAt: string
  dueDate?: string | null
  sourceId?: number | null
  sourceType: BankingValueObjectsPaymentRequestSourceTypeEnum
}

interface PaymentRequestItemDetailSidePanelProps {
  item: PaymentRequestItemData
  onClose: () => void
}

export const PaymentRequestItemDetailSidePanel = ({
  item,
  onClose,
}: PaymentRequestItemDetailSidePanelProps) => {
  const { navigation } = useNavigation()
  const sourcePath = getSourceNavigationPath(item.sourceType, item.sourceId, navigation)
  const sourceLabel = getSourceTypeLabel(item.sourceType)

  const listItems = [
    {
      title: i18n.t('banking.attributes.status'),
      info: (
        <StatusLabel type={getStatusColor(item.status)}>
          {getStatusLabel(item.status)}
        </StatusLabel>
      ),
    },
    {
      title: i18n.t('banking.attributes.amount'),
      info: <AmountWithCurrency amount={item.amountCents} currency={item.currency} />,
    },
    {
      title: i18n.t('banking.attributes.beneficiary'),
      info: item.beneficiaryName,
    },
    {
      title: i18n.t('banking.attributes.account_number'),
      info: item.beneficiaryAccountNumber,
    },
    {
      title: i18n.t('banking.attributes.created_at'),
      info: dateBackendDateTimeToUI(item.createdAt),
    },
    ...(item.dueDate
      ? [
          {
            title: i18n.t('banking.attributes.due_date'),
            info: dateBackendDateTimeToUI(item.dueDate),
          },
        ]
      : []),
    {
      title: i18n.t('banking.attributes.resource'),
      info: sourcePath ? <Link.Router to={sourcePath}>{sourceLabel}</Link.Router> : sourceLabel,
    },
  ]

  return (
    <Sidepanel
      overlay
      boxPadding
      width="wide"
      title={item.beneficiaryName}
      open
      onClose={onClose}
      headerBorder
      closeLabel={i18n.t('common.close')}
    >
      <Box width="full" gap="s24">
        <CustomTwoColumnsList list={listItems} />
      </Box>
    </Sidepanel>
  )
}
